/**
 * Home — dashboard and landing page
 *
 * For unauthenticated users: landing state with sign-in CTA.
 * For authenticated users: dashboard showing last session preview,
 * emotion trail, stats, cooldown state, and session/journal links.
 *
 * Free tier UX flow:
 *   1. First visit  → Welcome card (shown once via localStorage)
 *   2. Between sessions (< 72h) → "Opens [day]" cooldown card
 *   3. After 72h cooldown → "Begin a new session" button
 *   4. After 3 sessions → Upgrade prompt
 *
 * Action stack order (authenticated): the "Before you begin" preparation
 * suggestion leads, then the in-progress resume card, then the tier-dependent
 * primary CTA. The preparation renders in every tier state — including
 * cooldown and free-tier-exhausted — so there is always a practice available.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { db } from '../lib/supabase'
import type { SessionData } from '../types/session'
import Onboarding from '../components/session/Onboarding'
import { InstallCard } from '../components/install/InstallCard'
import { redirectToCheckout } from '../lib/stripe'
import { suggestPreparation } from '../data/preparations'
import { localizePreparation, localizeSuggestionReason } from '../data/preparations.pl'
import { localizeEmotionLabel } from '../data/emotions.pl'
import { pluralCategory } from '../lib/plural'
import { pickResumable } from '../lib/sessionResume'
import { freeTierState } from '../lib/freeTier'

interface HomeProps {
  user: User | null
}

interface Profile {
  display_name: string | null
  subscription_tier: string | null
}

/** Consecutive-day session streak */
function calcStreak(sessions: SessionData[]): number {
  const completed = sessions
    .filter(s => s.is_complete)
    .map(s => new Date(s.created_at).toDateString())
  const unique = [...new Set(completed)]
  if (!unique.length) return 0

  const today = new Date()
  let streak = 0
  const cursor = new Date(today)

  for (let i = 0; i < 365; i++) {
    if (unique.includes(cursor.toDateString())) {
      streak++
      cursor.setDate(cursor.getDate() - 1)
    } else if (i === 0) {
      cursor.setDate(cursor.getDate() - 1)
      if (unique.includes(cursor.toDateString())) {
        streak++
        cursor.setDate(cursor.getDate() - 1)
      } else {
        break
      }
    } else {
      break
    }
  }
  return streak
}

/** Most frequent emotion from completed sessions */
function topEmotion(sessions: SessionData[]): { word: string; count: number } | null {
  const counts: Record<string, number> = {}
  sessions.filter(s => s.is_complete).forEach(s => {
    counts[s.emotion_label] = (counts[s.emotion_label] ?? 0) + 1
  })
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1])
  if (!sorted.length) return null
  return { word: sorted[0][0], count: sorted[0][1] }
}

/**
 * Formats when the cooldown ends into a human-readable string.
 * Within 24h: "today at 3:41 PM"
 * Further out: "Thursday, April 27"
 */
/*
 * These three helpers take `t` and the language rather than reading them from a
 * hook, because they are module-level functions and cannot use one. The
 * language matters as much as the strings: toLocale*String was pinned to
 * 'en-US', so a Polish user was shown English weekday and month names even
 * once every surrounding label was translated.
 */
type TFn = (key: string, opts?: Record<string, unknown>) => string

function formatCooldownDate(date: Date, t: TFn, lang: string): string {
  const diffMs = date.getTime() - Date.now()
  const diffHours = diffMs / (1000 * 60 * 60)
  if (diffHours < 24) {
    return t('home.todayAt', {
      time: date.toLocaleTimeString(lang, { hour: 'numeric', minute: '2-digit' }),
    })
  }
  return date.toLocaleDateString(lang, { weekday: 'long', month: 'long', day: 'numeric' })
}

/** Human-readable time since a session: "Today", "Yesterday", "3 days ago" */
function formatTimeAgo(date: Date, t: TFn): string {
  const diffMs = Date.now() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return t('home.today')
  if (diffDays === 1) return t('home.yesterday')
  if (diffDays < 7) return t('home.daysAgo', { n: diffDays })
  if (diffDays < 14) return t('home.lastWeek')
  if (diffDays < 30) return t('home.weeksAgo', { n: Math.floor(diffDays / 7) })
  return t('home.monthsAgo', { n: Math.floor(diffDays / 30) })
}

/**
 * Contextual subtitle under the welcome heading.
 * Changes based on how long since the last session.
 */
function contextualSubtitle(lastSession: SessionData | null, t: TFn): string {
  if (!lastSession) return t('home.tagline')
  const diffMs = Date.now() - new Date(lastSession.created_at).getTime()
  const diffDays = diffMs / (1000 * 60 * 60 * 24)
  if (diffDays < 1)  return t('home.stillIntegrating')
  if (diffDays < 3)  return t('home.stillProcessing')
  if (diffDays < 8)  return t('home.readyToLook')
  return t('home.beenAWhile')
}

/**
 * Truncates a session summary to a short preview line.
 * Breaks at a word boundary so the clip point isn't mid-word.
 */
function truncateSummary(text: string | null, maxLen = 110): string | null {
  if (!text) return null
  if (text.length <= maxLen) return text
  const cut = text.slice(0, maxLen)
  const lastSpace = cut.lastIndexOf(' ')
  return (lastSpace > 70 ? cut.slice(0, lastSpace) : cut) + '...'
}

/** Progress dots for free tier: filled = completed, empty = remaining */
function FreeSessionDots({ completed }: { completed: number }) {
  return (
    <div className="flex items-center justify-center gap-1.5">
      {[0, 1, 2].map(i => (
        <div key={i} className="rounded-full transition-all" style={{
          width: i < completed ? 9 : 6,
          height: i < completed ? 9 : 6,
          background: i < completed ? '#9B72C0' : '#E4D5EE',
        }} />
      ))}
    </div>
  )
}

export default function Home({ user }: HomeProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const [sessions, setSessions] = useState<SessionData[]>([])
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [firstName, setFirstName] = useState<string | null>(null)
  const [tier, setTier] = useState<string>('free')
  const [profileLoading, setProfileLoading] = useState(true)
  // Soft-launch discovery banner: shown once to premium users; dismissed via localStorage
  const [arcsBannerDismissed, setArcsBannerDismissed] = useState(
    () => !!localStorage.getItem('ia_arcs_launched')
  )
  function dismissArcsBanner() {
    localStorage.setItem('ia_arcs_launched', '1')
    setArcsBannerDismissed(true)
  }
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  // Annual is pre-selected: approved 2026-09-05, PRICING_TQO_TIA.md section 12
  // ("one price at a time, annual pre-selected, monthly shown as the comparison").
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('year')

  // Cooldown tracking for free tier
  const [cooldownUntil, setCooldownUntil] = useState<Date | null>(null)

  // Welcome card: shown once to new free-tier users via localStorage flag
  const [showWelcomeCard, setShowWelcomeCard] = useState(false)

  // Load profile — name + tier
  useEffect(() => {
    if (!user) { setProfileLoading(false); return }

    db()
      .from('profiles')
      .select('display_name, subscription_tier')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        const profile = data as Profile | null
        const name = profile?.display_name ?? null
        const userTier = profile?.subscription_tier ?? 'free'
        setFirstName(name)
        setTier(userTier)
        setShowOnboarding(!name)

        // Show the welcome card once for all new users who have completed onboarding
        if (name && !localStorage.getItem('ia_welcome_v1')) {
          setShowWelcomeCard(true)
        }

        setProfileLoading(false)
      })
  }, [user])

  // Load recent sessions + compute cooldown for free-tier users
  useEffect(() => {
    if (!user) return
    setLoadingSessions(true)
    db()
      .from('sessions')
      .select('id, emotion_label, emotion_intensity, phase_reached, is_complete, session_summary, created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(10)
      .then(({ data }) => {
        const loaded = (data as SessionData[]) ?? []
        setSessions(loaded)
        setLoadingSessions(false)

        // Cooldown: free tier only. A session counts once its summary is written
        // (lib/freeTier.ts), the same rule as the session page and the server.
        if (tier === 'free') {
          const { cooldownUntil: until } = freeTierState(loaded)
          if (until) setCooldownUntil(until)
        }
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tier])

  function handleOnboardingComplete(name: string) {
    setFirstName(name)
    setShowOnboarding(false)
    // Show welcome card immediately after onboarding if not yet seen
    if (!localStorage.getItem('ia_welcome_v1')) {
      setShowWelcomeCard(true)
    }
  }

  function dismissWelcomeCard() {
    localStorage.setItem('ia_welcome_v1', 'seen')
    setShowWelcomeCard(false)
  }

  if (user && profileLoading) {
    return (
      <div className="min-h-svh flex items-center justify-center bg-[#FAF7F4]">
        <div className="w-5 h-5 rounded-full border-2 border-[#3D1F62] border-t-transparent animate-spin" />
      </div>
    )
  }

  const completedSessions = sessions.filter(s => s.is_complete)
  const completedCount = completedSessions.length
  const lastCompletedSession = completedSessions[0] ?? null
  const isFreeTierFull = tier === 'free' && freeTierState(sessions).full
  const isInCooldown = tier === 'free' && cooldownUntil !== null
  const streak = calcStreak(sessions)
  const top = topEmotion(sessions)
  /*
   * The rule logic picks WHICH practice to suggest; localization only changes
   * how it reads. Both the practice and its reason sentence are translated
   * here so the "Before you begin" card is fully in the user's language.
   */
  const rawSuggestion = suggestPreparation(
    lastCompletedSession ? new Date(lastCompletedSession.created_at) : null,
    lastCompletedSession?.emotion_intensity ?? 5,
  )
  const prepSuggestion = {
    preparation: localizePreparation(rawSuggestion.preparation, i18n.language),
    reason: localizeSuggestionReason(rawSuggestion.reason, i18n.language),
  }

  return (
    <>
      {/* Onboarding overlay — shown once for new users */}
      {showOnboarding && user && (
        <Onboarding
          userId={user.id}
          onComplete={handleOnboardingComplete}
        />
      )}

      {/* Welcome card — shown once to new free users, explains the 3-session structure */}
      {showWelcomeCard && !showOnboarding && (
        <div
          className="fixed inset-0 z-40 flex items-end justify-center px-4 pb-6"
          style={{ background: 'rgba(28,17,40,0.45)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className="w-full max-w-sm rounded-2xl px-6 py-7 message-appear"
            style={{ background: 'white', boxShadow: '0 8px 40px rgba(61,31,98,0.18)' }}
          >
            <p className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: '#9B72C0' }}>
              {t('home.beforeYouBegin')}
            </p>
            <p className="font-display text-lg leading-snug mb-3" style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}>
              {t('home.welcomeHeading')}
            </p>
            <p className="text-sm leading-relaxed mb-5" style={{ color: '#6B5F7A' }}>
              {t('home.welcomeBody')}
            </p>

            {/* Free-tier pacing note */}
            {tier === 'free' && (
              <div className="mb-5 rounded-xl px-4 py-3" style={{ background: 'rgba(155,114,192,0.06)', border: '1px solid #E4D5EE' }}>
                <div className="flex items-center gap-2 mb-1.5">
                  {[0, 1, 2].map(i => (
                    <div key={i} className="rounded-full" style={{ width: 7, height: 7, background: '#E4D5EE' }} />
                  ))}
                  <span className="text-xs" style={{ color: '#9B8FAA' }}>{t('home.sessionOneReady')}</span>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: '#9B8FAA' }}>
                  {t('home.freeAccessNote')}
                </p>
              </div>
            )}

            <button
              onClick={dismissWelcomeCard}
              className="w-full py-3.5 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('home.gotItBegin')}
            </button>
          </div>
        </div>
      )}

      <div
        className="min-h-svh flex flex-col"
        style={{
          background: 'radial-gradient(ellipse at 60% 0%, rgba(155,114,192,0.1) 0%, transparent 50%), radial-gradient(ellipse at 5% 70%, rgba(74,128,192,0.07) 0%, transparent 40%), #FAF7F4',
        }}
      >

        {/* Header */}
        <header className="flex items-center justify-between px-6 py-5 max-w-xl mx-auto w-full">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[#3D1F62] flex items-center justify-center">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="2.5" fill="white" opacity="0.9" />
                <circle cx="6" cy="6" r="5" stroke="white" strokeWidth="1" opacity="0.4" />
              </svg>
            </div>
            <span className="font-display text-[#1C1128] text-sm tracking-wide">{t('home.appName')}</span>
          </div>
          {user && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <button
                onClick={() => navigate('/profile')}
                aria-label={t('home.account')}
                className="hover:opacity-70 transition-opacity"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9B8FAA' }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M3 17c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          )}
        </header>

        {/* Main content */}
        <main
          className="flex-1 flex flex-col items-center px-6 pb-20 pt-4 page-enter"
          style={{ position: 'relative' }}
        >
          {/* Decorative blobs */}
          <div style={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(155,114,192,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: 80, left: -60, width: 180, height: 180, borderRadius: '50%', background: 'radial-gradient(circle, rgba(74,128,192,0.09) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', top: '40%', right: -20, width: 140, height: 140, borderRadius: '50%', background: 'radial-gradient(circle, rgba(184,125,10,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />

          <div className="w-full max-w-sm" style={{ position: 'relative', zIndex: 1 }}>

            {/* Home-screen nudge: phones only, from the second visit, until
                dismissed or installed. Renders nothing otherwise
                (src/lib/install/memory.ts). */}
            <InstallCard />

            {/* ── Healing Arcs soft-launch banner ───────────────────── */}
            {/* Shown once to Awakening users via localStorage flag.     */}
            {/* Dismissed on click or × — never reappears.              */}
            {tier === 'premium' && !arcsBannerDismissed && (
              <div
                className="mb-5 rounded-2xl px-4 py-4 flex items-start gap-3 message-appear"
                style={{
                  background: 'rgba(155,114,192,0.08)',
                  border: '1.5px solid rgba(155,114,192,0.22)',
                }}
              >
                <span style={{ color: '#9B72C0', fontSize: 14, flexShrink: 0, marginTop: 2 }}>✦</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium mb-0.5" style={{ color: '#1C1128' }}>
                    {t('home.arcsReady')}
                  </p>
                  <p className="text-xs leading-relaxed mb-2" style={{ color: '#6B5F7A' }}>
                    {t('home.arcsReadyBody')}
                  </p>
                  <button
                    onClick={() => { dismissArcsBanner(); navigate('/healing-arcs') }}
                    className="text-xs font-medium transition-opacity hover:opacity-70"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9B72C0', padding: 0 }}
                  >
                    {t('home.beginTheArcs')}
                  </button>
                </div>
                <button
                  onClick={dismissArcsBanner}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C4B5D4', padding: 0, flexShrink: 0 }}
                  aria-label={t('home.dismiss')}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            )}

            {/* ── Welcome heading + contextual subtitle ─────────────── */}
            <div className="text-center mb-6">
              <h1 className="font-display text-2xl text-[#1C1128] font-normal mb-1.5">
                {completedCount > 0
                  ? (firstName ? t('home.greeting', { name: firstName }) : t('home.welcomeBack'))
                  : (firstName ? t('home.greetingFirst', { name: firstName }) : t('home.welcome'))
                }
              </h1>
              {!loadingSessions && (
                <p className="text-sm" style={{ color: '#9B8FAA' }}>
                  {contextualSubtitle(lastCompletedSession, t)}
                </p>
              )}
            </div>

            {/* ── Last session preview — hero card for returning users ─ */}
            {!loadingSessions && lastCompletedSession && (
              <button
                onClick={() => navigate('/journal')}
                className="w-full mb-4 rounded-2xl text-left transition-all hover:opacity-90 active:scale-[0.99]"
                style={{
                  background: 'rgba(196,82,114,0.04)',
                  borderTop: '3px solid #C45272',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '1.5px solid #E4D5EE',
                  cursor: 'pointer',
                }}
              >
                <div className="px-5 py-4">
                  {/* Supertitle + time-ago */}
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium uppercase tracking-widest" style={{ color: '#C45272' }}>
                      {t('home.lastSession')}
                    </p>
                    <p className="text-xs" style={{ color: '#C4B5D4' }}>
                      {formatTimeAgo(new Date(lastCompletedSession.created_at), t)}
                    </p>
                  </div>

                  {/* Emotion + intensity */}
                  <p className="font-display text-xl leading-snug mb-2" style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}>
                    {localizeEmotionLabel(lastCompletedSession.emotion_label, i18n.language)}
                    <span className="text-sm not-italic ml-2" style={{ color: '#C4B5D4' }}>
                      · {lastCompletedSession.emotion_intensity}/10
                    </span>
                  </p>

                  {/* Session summary preview */}
                  {truncateSummary(lastCompletedSession.session_summary) && (
                    <p className="text-sm leading-relaxed mb-3" style={{ color: '#6B5F7A' }}>
                      {truncateSummary(lastCompletedSession.session_summary)}
                    </p>
                  )}

                  {/* View link */}
                  <div className="flex items-center justify-end gap-1">
                    <span className="text-xs font-medium" style={{ color: '#C45272' }}>{t('home.viewInJournal')}</span>
                    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" style={{ color: '#C45272', flexShrink: 0 }}>
                      <path d="M4 7h6M7.5 4.5L10 7l-2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </button>
            )}

            {/* ── Emotion trail — the thread across sessions ─────────── */}
            {!loadingSessions && completedSessions.length > 1 && (
              <div className="w-full mb-4">
                <p className="text-xs text-center mb-2" style={{ color: '#C4B5D4' }}>{t('home.yourRecentWork')}</p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {completedSessions.slice(0, 6).map(s => (
                    <span
                      key={s.id}
                      className="text-xs px-2.5 py-1 rounded-full"
                      style={{
                        background: 'rgba(155,114,192,0.08)',
                        color: '#7B5EA8',
                        border: '1px solid rgba(155,114,192,0.15)',
                      }}
                    >
                      {localizeEmotionLabel(s.emotion_label, i18n.language)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* ── Stats row ─────────────────────────────────────────── */}
            {!loadingSessions && completedCount > 0 && (
              <div
                className="flex justify-center gap-6 mb-5 w-full px-5 py-4 rounded-2xl"
                style={{
                  background: 'rgba(155,114,192,0.04)',
                  borderTop: '3px solid #9B72C0',
                  borderRight: '1.5px solid #E4D5EE',
                  borderBottom: '1.5px solid #E4D5EE',
                  borderLeft: '1.5px solid #E4D5EE',
                }}
              >
                <div className="text-center">
                  <p className="font-display text-3xl" style={{ color: '#3D1F62', fontWeight: 400, lineHeight: 1 }}>{completedCount}</p>
                  <p className="text-xs mt-1" style={{ color: '#9B8FAA' }}>{t(`home.sessionsLabel.${pluralCategory(completedCount, i18n.language)}`)}</p>
                </div>
                {streak > 0 && (
                  <div className="text-center">
                    <p className="font-display text-3xl" style={{ color: '#3D1F62', fontWeight: 400, lineHeight: 1 }}>{streak}</p>
                    <p className="text-xs mt-1" style={{ color: '#9B8FAA' }}>{t(`home.streakLabel.${pluralCategory(streak, i18n.language)}`)}</p>
                  </div>
                )}
                {top && completedCount >= 3 && (
                  <div className="text-center" style={{ maxWidth: 72 }}>
                    <p className="font-display text-sm leading-snug" style={{ color: '#3D1F62', fontWeight: 400, lineHeight: 1.2 }}>
                      {localizeEmotionLabel(top.word, i18n.language)}
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#9B8FAA' }}>{t('home.mostReturned')}</p>
                  </div>
                )}
              </div>
            )}


            {loadingSessions && <div className="mb-6" />}

            {/* ── Preparation suggestion — leads the action stack ─────
                Sits ABOVE both the in-progress resume card and the primary
                CTA: "Before you begin" is a preface, so it reads first.
                Hoisted out of the session-ready branch so it also appears
                during the free-tier cooldown and once the 3 free sessions
                are used — states where a preparation is the only practice
                still open to the user.
                Gated on !loadingSessions because suggestPreparation() keys
                off the last completed session; rendering early would show
                the no-history default and then visibly swap. ────────── */}
            {!loadingSessions && (
              <div className="w-full mb-4">
                <button
                  onClick={() => navigate(`/preparation/${prepSuggestion.preparation.id}`)}
                  className="w-full text-left rounded-2xl transition-all hover:opacity-90 active:scale-[0.99] mb-2"
                  style={{
                    background: prepSuggestion.preparation.colorSoft,
                    borderTop: `3px solid ${prepSuggestion.preparation.color}`,
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '1.5px solid #E4D5EE',
                    cursor: 'pointer',
                  }}
                >
                  <div className="px-5 py-4">
                    {/* Header row */}
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-xs font-medium uppercase tracking-widest" style={{ color: prepSuggestion.preparation.color }}>
                        {t('home.beforeYouBegin')}
                      </p>
                      {/* Duration badge */}
                      <div
                        className="flex items-center gap-1 px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: 'rgba(255,255,255,0.7)', border: `1px solid ${prepSuggestion.preparation.color}30` }}
                      >
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, color: prepSuggestion.preparation.color }}>
                          <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
                          <path d="M6 3.5V6l1.5 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="text-xs font-medium" style={{ color: prepSuggestion.preparation.color }}>
                          {prepSuggestion.preparation.durationMinutes} min
                        </span>
                      </div>
                    </div>

                    {/* Name + reason */}
                    <p className="font-display text-xl leading-snug mb-1" style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}>
                      {prepSuggestion.preparation.name}
                    </p>
                    <p className="text-sm leading-relaxed mb-3" style={{ color: '#6B5F7A' }}>
                      {prepSuggestion.reason}
                    </p>

                    {/* CTA arrow */}
                    <div className="flex items-center justify-end gap-1">
                      <span className="text-xs font-medium" style={{ color: prepSuggestion.preparation.color }}>{t('home.beginPreparation')}</span>
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none" style={{ color: prepSuggestion.preparation.color, flexShrink: 0 }}>
                        <path d="M4 7h6M7.5 4.5L10 7l-2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  </div>
                </button>

                {/* See all link */}
                <div className="flex justify-center">
                  <button
                    onClick={() => navigate('/preparations')}
                    className="text-xs"
                    style={{ background: 'none', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
                  >
                    {t('home.seeAllPreparations')}
                  </button>
                </div>
              </div>
            )}

            {/* ── In-progress session resume ─────────────────────────── */}
            {!loadingSessions && pickResumable(sessions) && (() => {
              const inProgress = pickResumable(sessions)!
              return (
                <div
                  className="w-full mb-4 rounded-xl px-5 py-4 flex items-center gap-3"
                  style={{
                    background: 'rgba(155,114,192,0.04)',
                    borderTop: '3px solid #9B72C0',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '1.5px solid #E4D5EE',
                  }}
                >
                  <div className="w-2 h-2 rounded-full flex-shrink-0 animate-pulse" style={{ background: '#9B72C0' }} />
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium" style={{ color: '#1C1128' }}>{t('home.sessionInProgress')}</p>
                    <p className="text-xs" style={{ color: '#9B8FAA' }}>
                      {localizeEmotionLabel(inProgress.emotion_label, i18n.language)} · {t('home.phaseOf', { n: inProgress.phase_reached })}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/session', { state: { resumeSession: { id: inProgress.id, phaseReached: inProgress.phase_reached } } })}
                    className="text-xs px-3 py-1.5 rounded-lg font-medium flex-shrink-0"
                    style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
                  >
                    {t('home.resume')}
                  </button>
                </div>
              )
            })()}

            {/* ── Primary CTA: varies by tier state ─────────────────── */}

            {isFreeTierFull ? (
              // All 3 free sessions done — upgrade prompt
              <div className="w-full mb-6">
                <div className="flex justify-center mb-3">
                  <FreeSessionDots completed={3} />
                </div>
                <p className="text-xs mb-4 text-center" style={{ color: '#9B8FAA' }}>{t('home.allFreeComplete')}</p>

                {/* Billing interval toggle */}
                <div className="flex mb-3 p-0.5 rounded-xl" style={{ background: '#F4EFF8', border: '1px solid #E4D5EE' }}>
                  <button
                    onClick={() => setBillingInterval('month')}
                    className="flex-1 py-2 rounded-xl text-xs font-medium transition-all"
                    style={{
                      background: billingInterval === 'month' ? '#3D1F62' : 'transparent',
                      color: billingInterval === 'month' ? 'white' : '#9B8FAA',
                      border: 'none', cursor: 'pointer',
                    }}
                  >
                    {t('home.monthly')}
                  </button>
                  <button
                    onClick={() => setBillingInterval('year')}
                    className="flex-1 py-2 rounded-xl text-xs font-medium transition-all"
                    style={{
                      background: billingInterval === 'year' ? '#3D1F62' : 'transparent',
                      color: billingInterval === 'year' ? 'white' : '#9B8FAA',
                      border: 'none', cursor: 'pointer',
                    }}
                  >
                    {t('home.annualHalfPrice')}
                  </button>
                </div>

                <button
                  onClick={async () => {
                    setCheckoutLoading(true)
                    setCheckoutError(null)
                    const { error } = await redirectToCheckout('growth', billingInterval)
                    if (error) { setCheckoutError(error); setCheckoutLoading(false) }
                  }}
                  disabled={checkoutLoading}
                  className="w-full py-4 rounded-xl font-medium tracking-wide mb-2"
                  style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: checkoutLoading ? 'wait' : 'pointer' }}
                >
                  {checkoutLoading
                    ? t('home.openingCheckout')
                    : billingInterval === 'year'
                      ? t('home.upgradeAnnual')
                      : t('home.upgradeMonthly')}
                </button>
                {billingInterval === 'month' && (
                  <p className="text-xs text-center" style={{ color: '#C4B5D4' }}>{t('home.orAnnualSave')}</p>
                )}
                {checkoutError && (
                  <p className="text-xs mt-2 text-center" style={{ color: '#f87171' }}>{checkoutError}</p>
                )}
              </div>
            ) : isInCooldown ? (
              // In 72h cooldown — show opening date
              <div className="w-full mb-6">
                <div
                  className="w-full rounded-2xl px-5 py-5 mb-4 text-center"
                  style={{
                    background: 'rgba(74,128,192,0.04)',
                    borderTop: '3px solid #4A80C0',
                    borderRight: '1.5px solid #E4D5EE',
                    borderBottom: '1.5px solid #E4D5EE',
                    borderLeft: '1.5px solid #E4D5EE',
                  }}
                >
                  <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#4A80C0' }}>
                    {t('home.yourNextSession')}
                  </p>
                  <div className="flex justify-center mb-3">
                    <FreeSessionDots completed={completedCount} />
                  </div>
                  <p className="text-xs font-medium mb-2" style={{ color: '#9B8FAA' }}>
                    {t('home.sessionOfThree', { n: completedCount + 1 })}
                  </p>
                  <p className="font-display text-lg leading-snug mb-1" style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}>
                    {t('home.opensOn', { when: formatCooldownDate(cooldownUntil!, t, i18n.language) })}
                  </p>
                  <p className="text-xs mt-2 leading-relaxed" style={{ color: '#C4B5D4' }}>
                    {t('home.giveYourselfTime')}
                    {t('home.stillWorking')}
                  </p>
                </div>
              </div>
            ) : (
              // Normal: session ready
              <div className="w-full mb-6">
                {/* Free tier progress dots */}
                {tier === 'free' && completedCount > 0 && (
                  <div className="flex flex-col items-center gap-1 mb-4">
                    <FreeSessionDots completed={completedCount} />
                    <p className="text-xs" style={{ color: '#9B8FAA' }}>
                      {t('home.sessionOfThree', { n: completedCount + 1 })}
                    </p>
                  </div>
                )}
                <button
                  onClick={() => navigate('/session', { state: { fresh: true } })}
                  className="w-full py-4 rounded-xl bg-[#3D1F62] text-white font-medium tracking-wide hover:bg-[#4A2875] active:scale-[0.98] transition-all"
                >
                  {t('home.beginSession')}
                </button>
              </div>
            )}

            {/* ── The Thread card — Practice+ only ───────────────────── */}
            {tier !== 'free' && (
              <div
                style={{
                  marginTop: '1.25rem',
                  borderRadius: '1.25rem',
                  background: 'linear-gradient(135deg, rgba(155,114,192,0.07), rgba(74,128,192,0.07))',
                  border: '1.5px solid #E4D5EE',
                  padding: '1.25rem 1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/thread')}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && navigate('/thread')}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9B72C0', margin: '0 0 0.25rem' }}>
                    {t('home.threadLabel')}
                  </p>
                  <p style={{ fontSize: '0.9375rem', color: '#1C1128', fontWeight: 500, margin: '0 0 0.2rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t('home.checkInToday')}
                  </p>
                  <p style={{ fontSize: '0.8rem', color: '#9B8FAA', margin: 0 }}>
                    {t('home.mostPresent')}
                  </p>
                </div>
                <div style={{ flexShrink: 0, width: '2.25rem', height: '2.25rem', borderRadius: '50%', background: '#3D1F62', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M5 11l4-4-4-4" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            )}

            {/* Your Pattern card — Awakening tier only, after 7+ entries */}
            {(tier === 'premium' || tier === 'awakening') && (
              <button
                onClick={() => navigate('/pattern')}
                style={{
                  width: '100%', marginTop: '0.75rem', padding: '0.875rem 1.25rem',
                  borderRadius: '1rem', background: 'none',
                  border: '1.5px solid #E4D5EE', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  textAlign: 'left',
                }}
              >
                <div>
                  <p style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#7B40A8', margin: '0 0 0.2rem' }}>
                    {t('home.yourPattern')}
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#6B5F7A', margin: 0 }}>
                    {t('home.threadAcrossTime')}
                  </p>
                </div>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 11l4-4-4-4" stroke="#9B8FAA" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}

            {/* ── Persistent nav row ──────────────────────────────────── */}
            {/* Always rendered regardless of tier / cooldown / upgrade    */}
            {/* state so users build muscle memory for where things live.  */}
            <div
              className="w-full flex items-center justify-center gap-1 mt-2"
              style={{ borderTop: '1px solid #EDE3F5', paddingTop: 20 }}
            >
              {/* Inner Sanctuary */}
              <button
                onClick={() => navigate('/journal')}
                className="flex flex-col items-center gap-1.5 flex-1 py-3 rounded-xl transition-opacity hover:opacity-70"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 2L2.5 7v9h4v-5h5v5h4V7L9 2z" stroke="#6B5F7A" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-[10px] font-medium" style={{ color: '#6B5F7A' }}>{t('home.innerSanctuary')}</span>
              </button>

              {/* Preparations */}
              <button
                onClick={() => navigate('/preparations')}
                className="flex flex-col items-center gap-1.5 flex-1 py-3 rounded-xl transition-opacity hover:opacity-70"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle cx="9" cy="9" r="7" stroke="#6B5F7A" strokeWidth="1.4" />
                  <path d="M9 5v4l2.5 2.5" stroke="#6B5F7A" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                <span className="text-[10px] font-medium" style={{ color: '#6B5F7A' }}>{t('home.preparations')}</span>
              </button>

              {/* Healing Arcs — active for Awakening, discovery for others */}
              <button
                onClick={() => navigate(tier === 'premium' ? '/healing-arcs' : '/upgrade/awakening')}
                className="flex flex-col items-center gap-1.5 flex-1 py-3 rounded-xl transition-opacity hover:opacity-70"
                style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: tier === 'premium' ? 1 : 0.5 }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 2c0 0-5 4-5 8.5a5 5 0 0010 0C14 6 9 2 9 2z" stroke="#6B5F7A" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9 9.5v3" stroke="#6B5F7A" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                <span className="text-[10px] font-medium" style={{ color: '#6B5F7A' }}>
                  {tier === 'premium' ? t('home.healingArcs') : `${t('home.healingArcs')} ✦`}
                </span>
              </button>
            </div>

          </div>
        </main>
      </div>
    </>
  )
}
